import {Chip, createStyles, Group, NumberInput, SegmentedControl, Select, Stack, Text} from '@mantine/core';
import React from 'react';

import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import InfoIcon from 'ui-library/icons/InfoIcon';

import {TimeTriggerConfig} from './RoutineTypes';
import {
  buildRunTime,
  DEFAULT_RUN_TIME_STRING,
  formatTimezoneAbbreviation,
  parseRunTime,
} from './RoutineUtils';

interface TimeTriggerConfigFormProps {
  config: Partial<TimeTriggerConfig>;
  onChange: (updates: Partial<TimeTriggerConfig>) => void;
}

// `describe` explains what the selected frequency means for the current interval, and is co-located
// with the option so the two can't drift. The wording reflects backend rrule behavior: an interval
// of N runs every N units, and monthly recurrences run on the same day-of-month the agent was set
// up on.
const FREQ_OPTIONS = [
  {
    value: 'daily',
    label: 'Day',
    describe: (interval: number) =>
      interval === 1 ? 'Runs every day at the set time.' : `Runs every ${interval} days at the set time.`,
  },
  {
    value: 'weekly',
    label: 'Week',
    describe: (interval: number) =>
      interval === 1
        ? 'Runs on the selected days of the week.'
        : `Runs on the selected days, every ${interval} weeks.`,
  },
  {
    value: 'monthly',
    label: 'Month',
    describe: (interval: number) =>
      interval === 1
        ? 'Runs once a month, on the same day of the month it was set up on.'
        : `Runs every ${interval} months, on the same day of the month it was set up on.`,
  },
] as const;

const FREQ_SELECT_DATA = FREQ_OPTIONS.map(({value, label}) => ({value, label}));

const DEFAULT_FREQ_TYPE = 'weekly';

function getFreqDescription(freqType: TimeTriggerConfig['freq_type'], interval: number): string {
  return FREQ_OPTIONS.find((option) => option.value === freqType)?.describe(interval) ?? '';
}

const DAY_OPTIONS = [
  {value: '1', label: 'Mon'},
  {value: '2', label: 'Tue'},
  {value: '3', label: 'Wed'},
  {value: '4', label: 'Thu'},
  {value: '5', label: 'Fri'},
  {value: '6', label: 'Sat'},
  {value: '0', label: 'Sun'},
];

// The "At" row mixes NumberInput and SegmentedControl, whose size tokens don't resolve to the
// same pixel height, so we pin the hour/minute inputs to this value. The SegmentedControl uses
// its `xs` token, which the theme already renders at this height (24px) without label clipping.
const AT_ROW_CONTROL_HEIGHT = 24;

// Pins the hour/minute inputs' height to the shared row height so they line up with the AM/PM toggle.
const AT_ROW_INPUT_STYLES = {
  input: {height: AT_ROW_CONTROL_HEIGHT, minHeight: AT_ROW_CONTROL_HEIGHT},
};

const useStyles = createStyles((theme) => ({
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap' as const,
  },
  fieldLabel: {
    fontSize: theme.fontSizes.md,
    fontWeight: 'bold',
    flexShrink: 0,
  },
  dayRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'nowrap',
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing.sm,
  },
  // Width is snug for a 1-2 digit value; the default input width is awkwardly wide here.
  timeInput: {
    width: 40,
  },
  pillChip: {
    '& .mantine-Chip-label': {
      borderRadius: 16,
      // The Chip's inner element is a <label>, which the monolith's global bootstrap CSS gives
      // `margin-bottom: 5px`. That margin pushes the pill up and leaves a gap below it, so we
      // zero it here to keep the pill vertically centered in the row.
      marginBottom: 0,
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      height: 'auto',
      fontSize: theme.fontSizes.sm,
      fontWeight: 500,
      cursor: 'pointer',
    },
    '& .mantine-Chip-iconWrapper': {
      display: 'none',
    },
  },
}));

export default function TimeTriggerConfigForm({config, onChange}: TimeTriggerConfigFormProps): JSX.Element {
  const {classes} = useStyles();

  const weeklyDaysAsStrings = (config.weekly_days ?? []).map((d) => String(d));
  const parsed = parseRunTime(config.run_time ?? DEFAULT_RUN_TIME_STRING);

  return (
    <Stack spacing="md">
      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>Repeat every</Text>
        <NumberInput
          value={config.recurrence_interval ?? 1}
          onChange={(val) => {
            onChange({recurrence_interval: typeof val === 'number' ? val : 1});
          }}
          min={1}
          max={52}
          size="sm"
          sx={{width: 64}}
        />
        <Select
          value={config.freq_type ?? DEFAULT_FREQ_TYPE}
          onChange={(val) => {
            if (val) {
              onChange({
                freq_type: val as TimeTriggerConfig['freq_type'],
                weekly_days: val === 'weekly' ? (config.weekly_days ?? []) : [],
              });
            }
          }}
          data={FREQ_SELECT_DATA}
          size="sm"
          sx={{width: 110}}
          // TODO(nweeden): Dropdown has extra gap due to global Popover withArrow default
        />
        <Popover
          renderContent={() => (
            <span>
              {getFreqDescription(config.freq_type ?? DEFAULT_FREQ_TYPE, config.recurrence_interval ?? 1)}
            </span>
          )}
          placement={FloatingPlacement.TOP}
          interaction={FloatingInteraction.HOVER}
        >
          <span aria-label="Frequency options help">
            <InfoIcon />
          </span>
        </Popover>
      </div>

      {(config.freq_type ?? DEFAULT_FREQ_TYPE) === 'weekly' && (
        <div className={classes.dayRow}>
          <Text className={classes.fieldLabel}>On</Text>
          <Chip.Group
            multiple={true}
            value={weeklyDaysAsStrings}
            onChange={(vals: Array<string>) => {
              onChange({weekly_days: vals.map((v) => Number(v))});
            }}
          >
            <div className={classes.chipGroup}>
              {DAY_OPTIONS.map((day) => (
                <Chip
                  key={day.value}
                  value={day.value}
                  size="md"
                  variant="filled"
                  className={classes.pillChip}
                >
                  {day.label}
                </Chip>
              ))}
            </div>
          </Chip.Group>
        </div>
      )}

      <Stack spacing="xs">
        <Group spacing="sm" align="center">
          <Text className={classes.fieldLabel}>At</Text>
          <Group spacing="xs" align="center">
            <NumberInput
              aria-label="Hour"
              value={parsed.hour12}
              onChange={(val) => {
                onChange({
                  run_time: buildRunTime(typeof val === 'number' ? val : 8, parsed.minute, parsed.period),
                });
              }}
              min={1}
              max={12}
              size="sm"
              className={classes.timeInput}
              styles={AT_ROW_INPUT_STYLES}
              hideControls={true}
            />
            <Text>:</Text>
            <NumberInput
              aria-label="Minute"
              value={parsed.minute}
              onChange={(val) => {
                onChange({
                  run_time: buildRunTime(parsed.hour12, typeof val === 'number' ? val : 0, parsed.period),
                });
              }}
              min={0}
              max={59}
              size="sm"
              className={classes.timeInput}
              styles={AT_ROW_INPUT_STYLES}
              hideControls={true}
              formatter={(value) => String(value).padStart(2, '0')}
            />
            <SegmentedControl
              aria-label="AM or PM"
              data={['AM', 'PM']}
              value={parsed.period}
              onChange={(val) => {
                onChange({run_time: buildRunTime(parsed.hour12, parsed.minute, val as 'AM' | 'PM')});
              }}
              // xs renders a 24px control (matches AT_ROW_CONTROL_HEIGHT) with no label clipping.
              size="xs"
            />
            {config.timezone != null && config.timezone !== '' && (
              <Text size="sm" color="dimmed">
                {formatTimezoneAbbreviation(config.timezone)}
              </Text>
            )}
          </Group>
        </Group>
        <Text size="md" c="dimmed" mb="sm">
          Your agent will start working at this time
        </Text>
      </Stack>
    </Stack>
  );
}
